import {TimelineItem} from "@/types/VueScheduler";
import {apiClient, t, useGlobalSetting} from "@/lib/utils";
import {supabase} from "@/api";

export interface Order {
    id: number,
    title: string,
    description: string,
    author: string,                    // Username
    work_group: string,                 // DB: work_group
    state: State,
    priority: string,                  // DB: priority_level enum ('1', '2', '3', '4')
    asset_id: string | null,            // DB: asset_id (AKZ)
    outage_code: string | null,         // DB: outage_code (St-Code)
    scheduled_start: string | null,     // DB: scheduled_start
    scheduled_end: string | null,       // DB: scheduled_end
    scheduled_duration: string | null,  // DB: scheduled_duration
    created_at?: string,                // DB: created_at
    updated_at?: string,                // DB: updated_at
    // Computed/Relations
    tasks?: Task[],
    progress?: number,                 // From orders_with_progress view
    task_count?: number,                // From orders_with_progress view
    completed_task_count?: number,       // From orders_with_progress view
    author_name?: string,               // From orders_with_progress view
}

export interface ListOrder {
    id: number,
    title: string,
    author: string,
    asset_id: string,
    scheduled_duration: string,
    scheduled_start: string,
}

export interface Task {
    uuid: string,
    id: number,
    order_id: number,                   // DB: order_id
    title: string,
    description: string,
    author: string,                    // Username
    assignee: string | null,           // DB: assignee (AGL)
    work_group: string,                 // DB: work_group
    state: State,
    priority: string,                  // DB: priority_level enum
    sequence: number,                  // DB: sequence (Reihenfolge)
    is_lockout: boolean,                // DB: is_lockout (Freischaltung)
    loto_area_id: number | null,        // DB: loto_area_id (Freischaltbereich)
    quick_done_by?: string | null,      // DB: quick_done_by (Schnell-Rückmeldung: wer)
    quick_done_at?: string | null,      // DB: quick_done_at (Schnell-Rückmeldung: wann)
    asset_id: string | null,            // DB: asset_id (AKZ)
    outage_code: string | null,         // DB: outage_code (St-Code)
    scheduled_start: string | null,     // DB: scheduled_start
    scheduled_end: string | null,       // DB: scheduled_end
    scheduled_duration: string | null,  // DB: scheduled_duration
    created_at?: string,                // DB: created_at
    updated_at?: string,                // DB: updated_at
    // Relations
    feedbacks?: Feedback[],
    // From tasks_with_details view
    order_title?: string,
    author_name?: string,
    assignee_name?: string,
    feedback_count?: number,
}

export interface ListTask {
    uuid: string,
    id: number,
    order_id: number,
    title: string,
    assignee: string,
    asset_id: string,
    work_group: string,
    state: State,
    scheduled_start: string,
    scheduled_duration: string,
    is_lockout: boolean,
    loto_area_id: number | null,
    quick_done_by?: string | null,
    quick_done_at?: string | null,
}

export interface TaskWithDetails extends Task {
    order_title?: string
    author_name?: string
    assignee_name?: string
}

export interface DashboardData {
    myTasks: Task[]
    myLockouts: Task[]
    stats: {
        allOrders: number
        allTasks: number
        openOrders: number
        finishedOrders: number
        openTasks: number
        finishedTasks: number
        overdueTasks: number
        inProgress: number
    }
    myStats: {
        weekCompleted: number
        weekOpen: number
        priorities: any[],
    }
    todayStats: {
        completed: number
        open: number
    }
    upcomingTasks: Task[]
}

export interface Feedback {
    id: number,
    task_id: number,              // DB: task_id
    author: string,
    text: string,
    duration: string | null,     // DB: INTERVAL
    is_final?: boolean,          // DB: is_final (Endrückmeldung – Aufgabe abschließen)
    created_at?: string,          // DB: created_at
    updated_at?: string,
    _isNew?: boolean,
}

export interface Group {
    id: number
    name: string,
    displayName: string,
    color: string,
    colorcode: string,
    icon: string,
    permission: number,
    executive: boolean,
    activeTasks?: number,
    totalTasks?: number,
}

export interface User {
    name: string,
    displayName: string,
    permission: number,
}

export interface reportOverview {
    aufgabenFailed: number,
    aufgabenFinished: number,
    aufgabenLength: number,
    auftragFailed: number,
    auftragFinished: number,
    auftragsLength: number,
    lockoutsFailed: number,
    lockoutsFinished: number,
    lockoutsLength: number
}

export interface TitleList {
    id: string,
    orderId?: string,
    title: string,
}

export interface Priority {
    level: number,
    title: string,
    icon: string,
    colorcode: string,
}

/*
* enum: maximoState
*
* {enum: description}
* */

export enum State {
    all = "",
    anfo = "ANFO",
    freigegeben = "FREIGEGEBEN",
    genehm = "GENEHM",
    wmat = "WMAT",
    term = "TERM",
    inbearb = "INBEARB",
    vorl_beend = "VORL_BEEND",
    beend = "BEEND",
    geschl = "GESCHL",
    wterm = "WTERM",
    histbearb = "HISTBEARB",
    zurueck = "ZURUEK",
    unerl_beend = "UNERL_BEEND",
    unerl_geschl = "UNERL_GENSCHL",
    geschlos = "GESCHLOS",
    sotrno = "SOTRNO"
}

export const StateDescription = {
    [State.all]: "Jeder Status",
    [State.anfo]: "Anforderung",
    [State.freigegeben]: "Freigegeben",
    [State.genehm]: "Genehmigt",
    [State.wmat]: "Warten auf Material",
    [State.term]: "Terminiert",
    [State.inbearb]: "In Bearbeitung",
    [State.vorl_beend]: "Vorläufig erledigt",
    [State.beend]: "Beendet",
    [State.geschl]: "Geschlossen",
    [State.wterm]: "Warten auf Terminierung",
    [State.histbearb]: "in Historie bearbeitet",
    [State.zurueck]: "Zurückgewiesen",
    [State.unerl_beend]: "Unerledigt Beendet",
    [State.unerl_geschl]: "Unerledigt Geschlossen",
    [State.geschlos]: "Geschlossen",
    [State.sotrno]: "Storniert"
};

export const StatePermission = {
    [State.all]: 3,
    [State.anfo]: 3,
    [State.freigegeben]: 2,
    [State.genehm]: 2,
    [State.wmat]: 2,
    [State.term]: 2,
    [State.inbearb]: 2,
    [State.vorl_beend]: 2,
    [State.beend]: 1,
    [State.geschl]: 2,
    [State.wterm]: 2,
    [State.histbearb]: 2,
    [State.zurueck]: 2,
    [State.unerl_beend]: 1,
    [State.unerl_geschl]: 2,
    [State.geschlos]: 2,
    [State.sotrno]: 2
};

export const StateColor = {
    [State.all]: "none",
    [State.anfo]: '#9E9E9E',          // Anforderung → Grau (neutral, am Anfang)
    [State.freigegeben]: '#C3D6EE',   // Freigegeben → Pro-Hellblau (freigegeben)
    [State.genehm]: '#2196F3',        // Genehmigt → Blau (offiziell genehmigt)
    [State.wmat]: '#FFC107',          // Wartet auf Material → Orange (blockiert)
    [State.term]: '#141689',          // Terminiert → Pro-Dunkelblau (geplant, wichtig)
    [State.inbearb]: '#1976D2',       // In Bearbeitung → Dunkelblau (aktiv)
    [State.vorl_beend]: '#9CCC65',    // Vorläufig erledigt → Hellgrün (Rückmeldung fehlt noch)
    [State.beend]: '#4CAF50',         // Beendet → Grün (erfolgreich)
    [State.geschl]: '#2E7D32',        // Geschlossen → Dunkelgrün (archiviert)
    [State.wterm]: '#FF9800',         // Wartet auf Terminierung → Orange (wartet)
    [State.histbearb]: '#757575',     // Historie bearbeitet → Dunkelgrau (alt)
    [State.zurueck]: '#D32F2F',       // Zurückgewiesen → Rot (abgelehnt)
    [State.unerl_beend]: '#D32F2F',   // Unerledigt beendet → Rot (fehlgeschlagen)
    [State.unerl_geschl]: '#C62828',  // Unerledigt geschlossen → Dunkelrot (failed & closed)
    [State.geschlos]: '#2E7D32',      // Geschlossen → Dunkelgrün (abgeschlossen)
    [State.sotrno]: '#B71C1C'         // Storniert → Dunkelrot (abgebrochen)
};

/*export enum Priority {
    a = "Sofort",
    b = "gemach",
    c = "lass dir zeit diggi",
}*/

export type PriorityLevel = 0 | 1 | 2 | 3 | 4;

export const PRIORITIES: Record<PriorityLevel, Priority> = {
    0: {
        level: -1,
        title: "-1",
        icon: "mdi-emoticon-poop",
        colorcode: "#b800ff"
    },
    1: {
        level: 1,
        title: "Sofort",
        icon: "mdi-alert-circle",
        colorcode: "#ff0000"
    },
    2: {
        level: 2,
        title: "innerhalb dieser Woche",
        icon: "mdi-alert",
        colorcode: "#ffae00"
    },
    3: {
        level: 3,
        title: "zu einem geplanten Termin",
        icon: "mdi-information",
        colorcode: "#5e5e5e"
    },
    4: {
        level: 4,
        title: "Füllarbeit",
        icon: "mdi-alert-circle",
        colorcode: "#286e14"
    }
}

export const getPriority = (level: number): Priority => {
    return PRIORITIES[level as PriorityLevel] ?? PRIORITIES[0];
}

export const getUserFromName = (user: string): User => {
    for (const us of Object.values(userList)) {
        if (user.toUpperCase() === us.name.toUpperCase()) {
            return us;
        }
    }
    return userList.none;
};

//TODO: userlist ordentlich mit backend abgleichen.
export const userList = {
    beckm: {
        name: "beckm",
        displayName: "Maurus Beck",
        permission: 1
    },
    maxm: {
        name: "maxm",
        displayName: "Max Mustermann",
        permission: 1
    },
    none: {
        name: "",
        displayName: "",
        permission: -1
    }
}

export const getUserList = async (): Promise<any[]> => {
        useGlobalSetting().loadingMessage = t("loading.userList");
    useGlobalSetting().isLoading = true;
    const {data, error} = await supabase.from('profiles').select("id, full_name");

    if (error) throw error
    useGlobalSetting().isLoading = false;
    return data;
}

const getRowFromGroup = (group: string): number => {
    if (group.toUpperCase() === Groups.produktion.name) {
        return 0;
    } else if (group.toUpperCase() === Groups.tde.name) {
        return 1;
    } else if (group.toUpperCase() === Groups.tdm.name) {
        return 2;
    } else return -1;
};

const getCSSClassFromGroup = (group: string): string => {
    return ""
}

export const getGroupFromName = (group: string): Group => {
    for (const g of Object.values(Groups)) {
        if (group.toUpperCase() === g.name.toUpperCase()) {
            return g;
        }
    }
    return Groups.none;
};

export const getColorFromGroup = (group: string): string => {
    return getGroupFromName(group).color;
}

export const getIconFromGroup = (group: string): string => {
    return getGroupFromName(group).icon;
}

export const Groups = {
    produktion: {
        id: 0,
        name: "PROD-PM3",
        displayName: "Produktion",
        color: "bg-PROD-PM3_color",
        colorcode: "#CE0058FF",
        icon: "mdi-paper-roll",
        permission: 1,
        executive: true,
    },
    tde: {
        id: 1,
        name: "TDE-PM3",
        displayName: "TDE",
        color: "bg-TDE-PM3_color",
        colorcode: "#C3D6EEFF",
        icon: "mdi-lightning-bolt",
        permission: 1,
        executive: true,
    },
    tdm: {
        id: 2,
        name: "TDM-PM3",
        displayName: "TDM",
        color: "bg-TDM-PM3_color",
        colorcode: "#A7A8AAFF",
        icon: "mdi-wrench",
        permission: 1,
        executive: true,
    },
    none: {
        id: -1,
        name: "none",
        displayName: "None",
        color: "bg_color_error",
        colorcode: "#FF0000FF",
        icon: "mdi-emoticon-poop",
        permission: -1,
        executive: false,
    },
    meister: {
        id: 3,
        name: "meister",
        displayName: "Meister",
        color: "bg-meister_color",
        colorcode: "#5EFF00FF",
        icon: "mdi-account-tie-outline",
        permission: 2,
        executive: false,
    },
    admin: {
        id: 99,
        name: "admin",
        displayName: "Admin",
        color: "bg-admin_color",
        colorcode: "#C800FFFF",
        icon: "mdi-crown",
        permission: 3,
        executive: false,
    }
}/*export const Groups = {
    produktion: {
        id: 0,
        name: "PROD-PM3",
        displayName: "Produktion",
        color: "bg_color_pro-rot",
        colorcode: "#CE0058FF",
        icon: "mdi-paper-roll",
        permission: 1
    },
    tde: {
        id: 1,
        name: "TDE-PM3",
        displayName: "TDE",
        color: "bg_color_pro-hellblau",
        colorcode: "#C3D6EEFF",
        icon: "mdi-lightning-bolt",
        permission: 1
    },
    tdm: {
        id: 2,
        name: "TDM-PM3",
        displayName: "TDM",
        color: "bg_color_pro-dunkelgrau",
        colorcode: "#A7A8AAFF",
        icon: "mdi-wrench",
        permission: 1
    },
    none: {
        id: -1,
        name: "none",
        displayName: "None",
        color: "bg_color_error",
        colorcode: "#FF0000FF",
        icon: "mdi-emoticon-poop",
        permission: -1
    },
    meister: {
        id: 3,
        name: "meister",
        displayName: "Meister",
        color: "bg_color_meister",
        colorcode: "#5EFF00FF",
        icon: "mdi-account-tie-outline",
        permission: 2
    },
    admin: {
        id: 99,
        name: "admin",
        displayName: "Admin",
        color: "bg_color_admin",
        colorcode: "#C800FFFF",
        icon: "mdi-crown",
        permission: 3
    }
}*/

export const generateGroupColors = () => {
    const colors: Record<string, string> = {}

    Object.values(Groups).forEach(group => {
        // Farbe mit dem Namen als Key hinzufügen
        colors[`${group.name}_color`] = group.colorcode

        // Optional: Auch eine bereinigte Version ohne Sonderzeichen
        /*        const cleanName = group.name.replace(/-/g, '_').toLowerCase()
                colors[`${cleanName}_color`] = group.colorcode*/
    })

    return colors
}

export const generateStateColors = () => {
    const colors: Record<string, string> = {}

    // Über alle State enum keys iterieren
    Object.keys(State).forEach(stateKey => {
        const state = State[stateKey as keyof typeof State]
        const color = StateColor[state]

        // Nur hinzufügen wenn eine gültige Farbe existiert
        if (color && color !== 'none') {
            // Key im Format: anfo_color, beend_color, etc.
            colors[`${stateKey}_color`] = color
        }
    })

    return colors
}

export const DEFAULT_TASK: Task = {
    uuid: "",
    id: -1,
    order_id: -1,
    title: "",
    description: "",
    author: "",
    assignee: null,
    work_group: "",
    state: State.anfo,
    priority: "3",
    sequence: 1,
    is_lockout: false,
    loto_area_id: null,
    asset_id: null,
    outage_code: null,
    scheduled_start: null,
    scheduled_end: null,
    scheduled_duration: null,
    feedbacks: [],
};

export const DEFAULT_ORDER: Order = {
    id: -1,
    title: "",
    description: "",
    author: "",
    work_group: "",
    state: State.anfo,
    priority: "3",
    asset_id: null,
    outage_code: null,
    scheduled_start: null,
    scheduled_end: null,
    scheduled_duration: null,
    tasks: [DEFAULT_TASK],
    progress: 0,
}

export const DEFAULT_DASHBOARDDATA: DashboardData = {
    myTasks: [
        DEFAULT_TASK
    ],
    myLockouts: [
        DEFAULT_TASK
    ],
    stats: {
        allOrders: -1,
        allTasks: -1,
        openOrders: -1,
        finishedOrders: -1,
        openTasks: -1,
        finishedTasks: -1,
        overdueTasks: -1,
        inProgress: -1
    },
    myStats: {
        weekCompleted: -1,
        weekOpen: -1,
        priorities: []
    },
    todayStats: {
        completed: -1,
        open: -1,
    },
    upcomingTasks: [
        DEFAULT_TASK
    ]
}

export const getLocalISOString = (date: Date): string => {
    const pad = (num: number) => num.toString().padStart(2, "0");

    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());

    return `${year}-${month}-${day}T${hours}:${minutes}`;
};


const getISOFromDuration = (start: string, duration: string, offset: string): [string, string] => {
    //console.log("info", start, duration, offset);
    const startDate = new Date(start);
    const [s_hours, s_minutes] = duration.split(":").map(Number);
    const durationTime = (60 * s_hours + s_minutes) * 60 * 1000;

    const [o_hours, o_minutes] = offset.split(":").map(Number);
    const offsetTime = (60 * o_hours + o_minutes) * 60 * 1000;

    const start_out = getLocalISOString(new Date(startDate.getTime() + offsetTime));

    const end_out = getLocalISOString(new Date(startDate.getTime() + durationTime + offsetTime));
    //console.log("start", start_out);
    //console.log("end", end_out);
    return [start_out, end_out];
}

/*export const convertAuftragToTimelineFormat = (auftrag: Order): {
    data: TimelineItem[],
    min: string,
    max: string
} => {
    const _list = (auftrag as any).tasks ?? auftrag;
    if (_list.length === 0) {
        return {data: [], min: "", max: ""};
    }

    const list = _list.sort((a: Task, b: Task) => {
        return a.sequence - b.sequence
    })

    let last: { start: string, end: string } = {start: "", end: ""};

    let _max: string = String(auftrag.scheduled_start);

    let _min: string = String(auftrag.scheduled_start);

    const sorted = list.map((aufgabe: Task, index: number) => {
        let out_start;
        let out_end;
        if (index == 0) {
            out_start = aufgabe.scheduled_start;
            out_end = getISOFromDuration(aufgabe.scheduled_start || "", aufgabe.scheduled_duration || "0:00", "0:00")[1];
        } else {
            if (list[index - 1].sequence < aufgabe.sequence) {
                // sequence is greater than previous
                const iso = getISOFromDuration(last.start, aufgabe.scheduled_duration || "0:00", list[index - 1].scheduledDuration || "0:00");
                out_start = iso[0];
                out_end = iso[1];
            } else {
                // sequence is same as previous (parallel tasks)
                out_start = aufgabe.scheduled_start;
                out_end = getISOFromDuration(last.start, aufgabe.scheduled_duration || "0:00", "0:00")[1];
            }
        }
        last = {start: out_start || "", end: out_end};
        if ((new Date(_max).getTime()) < (new Date(out_end).getTime())) {
            _max = out_end;
        }
        if ((new Date(_min).getTime()) > (new Date(out_start || "").getTime())) {
            _min = out_start || "";
        }
        return {
            row: getRowFromGroup(aufgabe.work_group),
            color: getGroupFromName(aufgabe.work_group).color,
            text: aufgabe.id.toString(),
            meta: {
                title: aufgabe.title,
                description: aufgabe.description,
                id: aufgabe.id,
                orderId: aufgabe.order_id,
            },
            start: out_start,
            end: out_end,
        }
    });

    if ((new Date(_max).getTime()) - (new Date(_min).getTime()) < 60 * 2 * 60 * 1000) {
        _max = getISOFromDuration(_min, "2:00", "0:00")[1];
    }

    return {data: sorted, min: _min, max: _max};
}*/

export const convertAuftragToTimelineFormat = (auftrag: Order): {
    data: TimelineItem[],
    min: string,
    max: string
} => {
    const _list = (auftrag as any).tasks ?? auftrag;
    if (_list.length === 0) {
        return {data: [], min: "", max: ""};
    }

    const list = _list.sort((a: Task, b: Task) => {
        return a.sequence - b.sequence
    })

    let last: { start: string, end: string } = {start: "", end: ""};

    let _max: string = String(auftrag.scheduled_start);

    let _min: string = String(auftrag.scheduled_start);

    const sorted = list.map((aufgabe: any, index: number) => {
        //console.log("order", aufgabe.order);
        let out_start;
        let out_end;
        if (index == 0) {
            out_start = aufgabe.scheduled_start;
            out_end = getISOFromDuration(aufgabe.scheduled_start, aufgabe.scheduled_duration, "0:00")[1];
        } else {
            if (list[index - 1].sequence < aufgabe.sequence) {
                //aufgabe.order ist größer als die davor
                const iso = getISOFromDuration(last.start, aufgabe.scheduled_duration, list[index - 1].scheduled_duration);
                out_start = iso[0];
                out_end = iso[1];
                //console.log(1, "start:" + iso[0], "end: " + iso[1])
            } else {
                //aufgabe.order ist (kleiner sollte nicht vorkomme, da die davor sortiert werden) oder gleich als die davor
                out_start = aufgabe.scheduled_start;
                out_end = getISOFromDuration(last.start, aufgabe.scheduled_duration, "0:00")[1];
                //console.log(2, "start: " + aufgabe.start, "end: " + getISOFromDuration(last.start, aufgabe.soll_duration, "0:00")[1])
            }
        }
        last = {start: out_start, end: out_end};
        //console.log("start", out_start, "end", out_end, "aufgabe", aufgabe);
        if ((new Date(_max).getTime()) < (new Date(out_end).getTime())) {
            //console.log("111")
            _max = out_end;
        }
        if ((new Date(_min).getTime()) > (new Date(out_start).getTime())) {
            //console.log("222")
            _min = out_start;
        }
        //console.log("--------------------------------")
        return {
            row: getRowFromGroup(aufgabe.work_group),
            color: getGroupFromName(aufgabe.work_group).color,
            text: aufgabe.id.toString(),
            meta: {
                title: aufgabe.title,
                description: aufgabe.description,
                id: aufgabe.id,
                parent_id: aufgabe.order_id,
            },
            start: out_start,
            end: out_end,
        }
    });

    /*const sortedByEnd = [...sorted].sort((a: TimelineItem, b: TimelineItem) => {
        return new Date(a.end).getTime() - new Date(b.end).getTime();
    });

    const sortedByStart = [...sorted].sort((a: TimelineItem, b: TimelineItem) => {
        return new Date(a.start).getTime() - new Date(b.start).getTime();
    });*/

    //const _min = sortedByStart[0].start;
    //let max = sortedByEnd[sortedByEnd.length - 1].end;

    if ((new Date(_max).getTime()) - (new Date(_min).getTime()) < 60 * 2 * 60 * 1000) {
        _max = getISOFromDuration(_min, "2:00", "0:00")[1];
    }

    //console.log("min:", _min);
    //console.log("max:", _max);

    return {data: sorted, min: _min, max: _max};
}

export const isStartable = (order: Order, id: number): boolean => {
    if (!order.tasks || order.tasks.length <= 1) return true

    const sorted = [...order.tasks].sort((a, b) => a.sequence - b.sequence)
    const currentIndex = sorted.findIndex(a => a.id === id)
    if (currentIndex === -1) return false

    const current = sorted[currentIndex]
    const currentSequence = current.sequence

    // All tasks with lower sequence must be completed
    // (VORL_BEEND zählt als erledigt – die physische Freischaltung ist gemacht)
    for (const task of sorted) {
        if (task.sequence < currentSequence && ![State.beend, State.vorl_beend].includes(task.state)) {
            return false
        }
    }

    return true
}

export const getOrderFromID = async (id: number): Promise<Order> => {
    useGlobalSetting().loadingMessage = t("loading.auftragsInfo")
    useGlobalSetting().isLoading = true;
    const {data, error} = await supabase
        .from('orders')
        .select(`
          *,
          tasks (
              *,
              feedbacks (*)
          )
      `)
        .eq('id', id)
        .order('sequence', {referencedTable: 'tasks', ascending: true}).order('id', {referencedTable: 'tasks.feedbacks', ascending: true})
        .single()

    if (error) throw error
    useGlobalSetting().isLoading = false;
    return data

    /*    useGlobalSetting().loadingMessage = t("loading.auftragsInfo")
        useGlobalSetting().isLoading = true;
        try {
            const response = await apiClient.get(`/getAuftrag/${id}`);
            useGlobalSetting().isLoading = false;
            return response.data
        } catch (error) {
            console.error('Fehler beim Laden des Auftrags:', error)
            return DEFAULT_ORDER
        }*/
}

/*export async function getAuftragFromId(orderId: number) {
    const {data, error} = await supabase
        .from('orders')
        .select(`
              *,
              tasks (
                  *,
                  feedbacks (*)
              )
          `)
        .eq('id', orderId)
        .single()

    if (error) throw error
    return data
}*/

/*export const getAufgabeFromIDs = async (id: number, parent_id: number): Promise<Order> => {
    useGlobalSetting().loadingMessage = t("loading.aufgabenInfo")
    useGlobalSetting().isLoading = true;
    try {
        const response = await apiClient.get(`/getAufgabe/${parent_id}/${id}`);
        useGlobalSetting().isLoading = false;
        return response.data
    } catch (error) {
        console.error('Fehler beim Laden der Aufgabe:', error)
        return DEFAULT_ORDER
    }
}*/

export const updateTask = async (record: Task): Promise<Order> => {
    useGlobalSetting().loadingMessage = t("loading.updateAufgabe")
    useGlobalSetting().isLoading = true;
    try {
        const response = await apiClient.put(`/updateAufgabe/${record.id}`, record);
        useGlobalSetting().isLoading = false;
        return response.data
    } catch (error) {
        console.error('Fehler beim Updaten der Aufgabe:', error)
        return DEFAULT_ORDER
    }
}

export const updateOrder = async (record: Order): Promise<Order> => {
    useGlobalSetting().loadingMessage = t("loading.updateAuftrag")
    useGlobalSetting().isLoading = true;
    try {
        const response = await apiClient.put(`/updateAuftrag/${record.id}`, record);
        useGlobalSetting().isLoading = false;
        return response.data
    } catch (error) {
        console.error('Fehler beim Updaten des Auftrags:', error)
        return DEFAULT_ORDER
    }
}

export const getDashboardData = async (): Promise<DashboardData> => {
    useGlobalSetting().loadingMessage = t("loading.getDashboardData")
    useGlobalSetting().isLoading = true;
    try {
        const response = await apiClient.get(`/getDashboardData`);
        useGlobalSetting().isLoading = false;
        return response.data
    } catch (error) {
        console.error('Fehler beim Updaten des Dashboards:', error)
        return DEFAULT_DASHBOARDDATA
    }
}